import type { ReactNode } from 'react';
import type { SxProps } from '@mui/material';

import { useState } from 'react';
import { InputAdornment, inputBaseClasses, Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { TextField, theme } from '@theguarantors/tg-package-material-ui';

import {
  CurrencyDecimalInput,
  CurrencyFixedDecimalInput,
  stripTrailingFractionalZeros,
} from '@/shared/formatters';
import { createDataTestId } from '@/utils/createDataTestId';
import { FieldHelperText } from '../../components/field-helper.text';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';

export type LeaseMoneyInputProps = {
  name: string;
  title: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
  fixedDecimalScale?: boolean;
  onBlurValue?: (value: string | undefined) => void;
  onChangeValue?: (value: string) => void;
};

export const LeaseMoneyInput = ({
  name,
  title,
  required = true,
  disabled = false,
  helperText,
  placeholder,
  fixedDecimalScale = false,
  onBlurValue,
  onChangeValue,
}: LeaseMoneyInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const { watch, setValue, trigger, getFieldState, formState } = useFormContext();
  const { inputSize: size, asteriskColor } = useLeaseDetailsUI();
  const resolvedAsteriskColor = asteriskColor ?? theme.palette.red[600];

  const watchValue = watch(name) as string | number | undefined;
  // `getFieldState` walks dotted paths (e.g. `applicants.0.rent`) where bare
  // bracket lookups on `formState.{errors,dirtyFields}` would return undefined.
  const fieldState = getFieldState(name, formState);
  const dirty = fieldState.isDirty;
  const value = getInputText(watchValue);

  const error = fieldState.error;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: nextValue } = event?.target ?? {};
    if (nextValue == null) return;

    // `fixedDecimalScale` pads the display to ".XX" but the form-state /
    // payload value should stay canonical — strip trailing fractional zeros
    // so "2000.00" stores as "2000" and "200.50" stores as "200.5".
    const storedValue = fixedDecimalScale ? stripTrailingFractionalZeros(nextValue) : nextValue;

    setValue(name, storedValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
    trigger(name);
    onChangeValue?.(storedValue);
  };

  const onBlur = () => {
    onBlurValue?.(typeof value === 'string' ? value : value?.toString());
    setIsFocused(false);
  };

  const sx = disabled ? styles : {};

  const inputLabel = !value || dirty ? inputAdornmentProps(dirty || isFocused) : {};

  const dataTestId = createDataTestId({ inputString: name, suffix: 'input' });

  return (
    <Stack direction="column" sx={{ width: '100%' }}>
      <TextField
        data-testid={dataTestId}
        color="secondary"
        disabled={disabled}
        error={!!error?.message}
        helperText={error?.message?.toString()}
        label={title}
        name={name}
        onFocus={() => setIsFocused(true)}
        onBlur={onBlur}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        size={size}
        slotProps={{
          inputLabel,
          input: getMoneyInputProps(fixedDecimalScale) as any,
        }}
        sx={{
          '& .MuiInputBase-root': {
            width: '100%',
          },
          '& .MuiFormLabel-asterisk': {
            color: `${resolvedAsteriskColor} !important`,
          },
          '& .MuiInputLabel-root.Mui-disabled .MuiFormLabel-asterisk': {
            color: 'inherit !important',
          },
          '& .MuiInputLabel-root': {
            [theme.breakpoints.down('md')]: { fontSize: '14px !important' },
          },
          width: '100%',
          ...sx,
        }}
        value={value}
      />
      {helperText && <FieldHelperText text={helperText} disabled={disabled} />}
    </Stack>
  );
};

const styles: SxProps = {
  '.MuiInputLabel-root': {
    color: theme.palette.neutralGrey[300],
    width: '100%',
  },
  '& .MuiFilledInput-input.Mui-disabled': {
    backgroundColor: 'white',
    border: `1px solid ${theme.palette.neutralGrey[200]}`,
    color: theme.palette.neutralGrey[300],
    borderRadius: '4px',
  },
};

const getInputText = (value: string | number | undefined) => {
  if (!value) return '';
  return value === '0' ? '' : value;
};

const getMoneyInputProps = (fixedDecimalScale: boolean) => ({
  disableUnderline: true,
  startAdornment: (
    <InputAdornment
      sx={{
        transition: 'margin 0.3s ease-in-out',
        [`[data-shrink=false] ~ .${inputBaseClasses.root} > &`]: {
          marginTop: '0 !important',
        },
      }}
      position="start"
    >
      $
    </InputAdornment>
  ),
  inputComponent: fixedDecimalScale ? CurrencyFixedDecimalInput : CurrencyDecimalInput,
});

const inputAdornmentProps = (isFocused: boolean | undefined) => ({
  shrink: isFocused || false,
  sx: {
    paddingLeft: isFocused ? '0px' : '16px',
  },
});
